import type { TravelTrip } from "@-/dev.improve.haniverse.database.schemas.actor.schema";

/** Trip `mode`s beyond the timetabled/estimated transport modes. */
export const START_TRAVEL_MODE = "start";
export const TELEPORT_TRAVEL_MODE = "teleport";

/** Where the map suggests starting: 中山, the city the first two levels unlock. */
export const SUGGESTED_START_ADCODE = 442_000;

export type TravelState =
  /** No starting place chosen yet. */
  | { kind: "unplaced"; visited: ReadonlySet<number> }
  | { adcode: number; kind: "at"; visited: ReadonlySet<number> }
  | {
      kind: "in-transit";
      /** 0–1 along the journey, by elapsed time. */
      progress: number;
      trip: TravelTrip;
      visited: ReadonlySet<number>;
    };

/** Deterministic across synced devices: departure time, then creation time, then id. */
function compareTrips(a: TravelTrip, b: TravelTrip): number {
  return (
    a.departedAt.localeCompare(b.departedAt) ||
    a.createdAt.localeCompare(b.createdAt) ||
    a.id.localeCompare(b.id)
  );
}

/**
 * Derives the learner's location from their live trips (the table is a ledger; nothing else is
 * stored). The latest trip decides it: cancelled → back at its origin; arrived → at its
 * destination; otherwise still travelling. Every arrived, uncancelled destination is visited.
 */
export function deriveTravelState(trips: readonly TravelTrip[], now: Date): TravelState {
  const nowIso = now.toISOString();
  const sorted = [...trips].sort(compareTrips);
  const visited = new Set<number>();
  for (const trip of sorted) {
    if (trip.cancelledAt === null && trip.arrivesAt <= nowIso) {
      visited.add(trip.destinationAdcode);
    }
  }
  const latest = sorted.at(-1);
  if (latest === undefined) {
    return { kind: "unplaced", visited };
  }
  if (latest.cancelledAt !== null) {
    return { adcode: latest.originAdcode, kind: "at", visited };
  }
  if (latest.arrivesAt <= nowIso) {
    return { adcode: latest.destinationAdcode, kind: "at", visited };
  }
  const departed = Date.parse(latest.departedAt);
  const total = Date.parse(latest.arrivesAt) - departed;
  return {
    kind: "in-transit",
    progress: total <= 0 ? 1 : Math.min(1, Math.max(0, (now.getTime() - departed) / total)),
    trip: latest,
    visited,
  };
}

/** A destination is reachable once its name is learned, or forever after a visit. */
export function canTravelTo({
  adcode,
  unlocked,
  visited,
}: {
  adcode: number;
  unlocked: boolean;
  visited: ReadonlySet<number>;
}): boolean {
  return unlocked || visited.has(adcode);
}
